import React, {useState} from 'react';
import {validateChangePasswordWithHookForm} from '@shared/modules/user/User';
import {changeMyPassword} from '@shared/services/UserService';
import {
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  InputLabel,
  Typography,
  makeStyles,
  createStyles,
  Theme
} from '@material-ui/core';
import './UserResetPassword.scss';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import {
  FONT_SECONDARY_COLOR,
  BORDER_PRIMARY_COLOR,
  APP
} from '../../../style/variable';
import {SnackBarConfig} from '../../../utils/SnackBarConfig';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import {useHistory} from 'react-router-dom';
import {BASE_ROUTE_ADMIN} from 'src/constants/route/BaseRoute';
import NavRoutes from 'src/constants/route/NavRoutes';

// For Show or Hide Button
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import InputAdornment from '@material-ui/core/InputAdornment';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
// For Show or Hide Button

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    FormLabel: {
      left: '-0.8rem',
      fontSize: '1.2rem',
      color: FONT_SECONDARY_COLOR
    },
    appBar: {
      color: theme.palette.text.primary,
      background: theme.palette.background.paper,
      boxShadow: 'none',
      [theme.breakpoints.up('md')]: {
        width: `100%`
      }
    },
    formWrapper: {
      // borderRadius: 0,
      marginTop: '1rem !important',
      '& .MuiOutlinedInput-notchedOutline ': {
        borderColor: BORDER_PRIMARY_COLOR,
        top: 0,
        '& legend': {
          display: 'none'
        }
      }
    },
    button: {
      fontFamily: APP.FONT_FAMILY.BOLD
    },
    resetTitle: {
      display: 'flex',
      left: 0,
      paddingBottom: '2.5rem',
      fontFamily: APP.FONT_FAMILY.MEDIUM,
      fontSize: '1.7rem'
    },
    subTitle: {
      display: 'flex',
      left: 0,
      paddingBottom: '1.5rem',
      fontFamily: APP.FONT_FAMILY.REGULAR,
      fontSize: '1.2rem'
    },
    resetButton: {
      justifyContent: 'center',
      display: 'flex',
      textAlign: 'center',
      alignItems: 'center'
    },
    copyRightText: {
      fontSize: '1rem'
    },
    logo: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      paddingBottom: '40px'
    }
  })
);

const ResetPasswordPostLogin = (): React.ReactElement => {
  const classes = useStyles();
  const history = useHistory();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [buttonsDisable, setButtonsDisable] = useState<boolean>(false);
  const {control, handleSubmit, errors} = useForm({
    mode: 'all',
    resolver: async values => {
      return {values, errors: validateChangePasswordWithHookForm(values)};
    }
  });

  const handleResetPasswordFormSubmit = async (values: any) => {
    try {
      setButtonsDisable(true);
      const reqBody: any = {
        old_password: values.oldpassword,
        new_password: values.newpassword,
        confirm_password: values.confirmPassword
      };
      changeMyPassword(reqBody)
        .then(res => {
          enqueueSnackbar(
            res && res.message
              ? res.message
              : t('RESET_PASSWORD.SET_PASSWORD_SUCCESS'),
            SnackBarConfig('s')
          );
          history.push(
            `/${BASE_ROUTE_ADMIN}/${NavRoutes.PRIVATE.HOME}/${NavRoutes.PRIVATE.ADMIN.CLINICS}`
          );
        })
        .catch(err => {
          enqueueSnackbar(
            err && err.message
              ? err.message
              : t('RESET_PASSWORD.SET_PASSWORD_ERROR'),
            SnackBarConfig('e')
          );
        })
        .finally(() => {
          setButtonsDisable(false);
        });
    } catch (err) {
      enqueueSnackbar(
        err && err.message
          ? err.message
          : t('RESET_PASSWORD.SET_PASSWORD_ERROR'),
        SnackBarConfig('e')
      );
    }
  };

  // For Show or Hide Pass
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState<boolean>(
    false
  );
  const [
    isCurrentPasswordVisible,
    setIsCurrentPasswordVisible
  ] = useState<boolean>(false);
  const [
    isConfirmPasswordVisible,
    setIsConfirmPasswordVisible
  ] = useState<boolean>(false);

  const handleClickShowCurrentPassword = () => {
    if (isCurrentPasswordVisible == true) {
      setIsCurrentPasswordVisible(false);
    } else {
      setIsCurrentPasswordVisible(true);
    }
  };

  const handleClickShowNewPassword = () => {
    if (isNewPasswordVisible == true) {
      setIsNewPasswordVisible(false);
    } else {
      setIsNewPasswordVisible(true);
    }
  };

  const handleClickShowConfirmPassword = () => {
    if (isConfirmPasswordVisible == true) {
      setIsConfirmPasswordVisible(false);
    } else {
      setIsConfirmPasswordVisible(true);
    }
  };

  // For Show or Hide Pass

  return (
    <div className="resetpassword-wrapper flex-centering">
      <div className="reset-userpassword-content-wrap">
        <Card className="card-wrapper">
          <CardContent className="card-content">
            <div className="resetpassword-form">
              <Typography className={classes.resetTitle} variant={'h3'}>
                {/* {t('RESET_PASSWORD.RESET_PASSWORD')} */}
                {t('RESET_PASSWORD.CHANGE_PASSWORD')}
              </Typography>
              <Typography className={classes.subTitle}>
                {t('RESET_PASSWORD.SUBTITLE1')}
              </Typography>
              <form onSubmit={handleSubmit(handleResetPasswordFormSubmit)}>
                <div style={{marginBottom: '1rem'}}>
                  <FormControl
                    error={!!errors.oldpassword}
                    fullWidth
                    variant="outlined">
                    <InputLabel shrink className={classes.FormLabel}>
                      {t('RESET_PASSWORD.PASSWORD')}*
                    </InputLabel>
                    <Controller
                      className={classes.formWrapper}
                      id="outlined-adornment-old-password"
                      as={StyledOutlinedInput}
                      margin={'dense'}
                      label={t('RESET_PASSWORD.PASSWORD')}
                      name={'oldpassword'}
                      control={control}
                      placeholder={t('RESET_PASSWORD.PASSWORD')}
                      // type={'password'}
                      type={isCurrentPasswordVisible ? 'text' : 'password'}
                      autoComplete="on"
                      defaultValue=""
                      error={!!errors.oldpassword}
                      // For Show or Hide Pass
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton onClick={handleClickShowCurrentPassword}>
                            {isCurrentPasswordVisible ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      // For Show or Hide Pass
                    />
                    {errors.oldpassword && (
                      <FormHelperText>{t(errors.oldpassword)}</FormHelperText>
                    )}
                  </FormControl>
                </div>
                <div>
                  <FormControl
                    error={!!errors.newpassword}
                    fullWidth
                    variant="outlined">
                    <InputLabel shrink className={classes.FormLabel}>
                      {t('RESET_PASSWORD.NEW_PASSWORD')}*
                      {/* {t('RESET_PASSWORD.NEW_PASSWORD')}* */}
                    </InputLabel>
                    <Controller
                      className={classes.formWrapper}
                      id="outlined-adornment-new-password"
                      as={StyledOutlinedInput}
                      margin={'dense'}
                      label={t('RESET_PASSWORD.NEW_PASSWORD')}
                      name={'newpassword'}
                      control={control}
                      placeholder={t('RESET_PASSWORD.NEW_PASSWORD')}
                      type={isNewPasswordVisible ? 'text' : 'password'}
                      autoComplete="on"
                      defaultValue=""
                      error={!!errors.newpassword}
                      // For Show or Hide Pass
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton onClick={handleClickShowNewPassword}>
                            {isNewPasswordVisible ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      // For Show or Hide Pass
                    />
                    {errors.newpassword && (
                      <FormHelperText>{t(errors.newpassword)}</FormHelperText>
                    )}
                  </FormControl>
                </div>
                <div style={{marginTop: '1rem'}}>
                  <FormControl
                    error={!!errors.confirmPassword}
                    fullWidth
                    variant="outlined">
                    <InputLabel shrink className={classes.FormLabel}>
                      {t('RESET_PASSWORD.CONFIRM_NEW_PASSWORD')}*
                    </InputLabel>
                    <Controller
                      className={classes.formWrapper}
                      id="outlined-adornment-confirm-new-password"
                      as={StyledOutlinedInput}
                      margin={'dense'}
                      label={t('RESET_PASSWORD.CONFIRM_NEW_PASSWORD')}
                      name={'confirmPassword'}
                      control={control}
                      placeholder={t('RESET_PASSWORD.CONFIRM_NEW_PASSWORD')}
                      type={isConfirmPasswordVisible ? 'text' : 'password'}
                      autoComplete="on"
                      defaultValue=""
                      error={!!errors.confirmPassword}
                      // For Show or Hide Pass
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton onClick={handleClickShowConfirmPassword}>
                            {isConfirmPasswordVisible ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      // For Show or Hide Pass
                    />
                    {errors.confirmPassword && (
                      <FormHelperText>
                        {t(errors.confirmPassword)}
                      </FormHelperText>
                    )}
                  </FormControl>
                </div>
                <div className={classes.resetButton}>
                  <Button
                    data-test-id="pcl-dashboard-changePasswordButton"
                    variant="contained"
                    size={'medium'}
                    color="primary"
                    className={['block', classes.button].join(' ')}
                    type={'submit'}
                    disabled={buttonsDisable}
                    onClick={handleSubmit(handleResetPasswordFormSubmit)}>
                    {t('RESET_PASSWORD.CHANGE_PASSWORD')}
                  </Button>
                </div>
              </form>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPostLogin;
